import type { MouseEvent } from 'react';
import { ChevronRight } from 'lucide-react';

import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import {
  mergeWithDefaultMentionCategories,
  type MentionCategoryDescriptor,
} from 'features/chat/utils/normalize-mention-search';
import type { MentionSearchType } from '../../types/mentions.service.type';
import { getMentionCategoryIcon } from './mention-picker.constants';
import { useTranslation } from 'react-i18next';

type MentionCategoriesMenuProps = {
  categories: MentionCategoryDescriptor[];
  isLoading: boolean;
  onSelectCategory: (type: MentionSearchType) => void;
};

function handleCategoryClick(
  event: MouseEvent<HTMLButtonElement>,
  type: MentionSearchType,
  onSelectCategory: (type: MentionSearchType) => void
) {
  event.preventDefault();
  event.stopPropagation();
  onSelectCategory(type);
}

export function MentionCategoriesMenu({
  categories,
  isLoading,
  onSelectCategory,
}: MentionCategoriesMenuProps) {
  const displayCategories = mergeWithDefaultMentionCategories(categories);
  const { t } = useTranslation();

  return (
    <div className="w-full max-w-[220px] overflow-hidden rounded-xl border border-border/60 bg-popover shadow-lg">
      <p className="px-3 pt-2.5 pb-1 text-xs text-muted-foreground">{t('CATEGORIES') || 'Categories'}</p>

      <div className="max-h-[240px] overflow-y-auto py-0.5">
        {displayCategories.map((category) => {
          const Icon = getMentionCategoryIcon(category.type);

          return (
            <button
              key={category.type}
              type="button"
              className={cn(
                'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors',
                'hover:bg-muted/80'
              )}
              onMouseDown={(event) => handleCategoryClick(event, category.type, onSelectCategory)}
              onClick={(event) => handleCategoryClick(event, category.type, onSelectCategory)}
            >
              <Icon className="h-4 w-4 shrink-0 text-primary dark:text-true-white" />
              <span className="min-w-0 flex-1 font-medium text-foreground">{category.label}</span>
              {isLoading ? (
                <Skeleton className="h-3.5 w-4 shrink-0 rounded-sm" />
              ) : (
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {category.count}
                </span>
              )}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          );
        })}
      </div>

      <p className="border-t border-border/50 px-3 py-2.5 text-xs text-balance text-muted-foreground">
        {t('OR_CONTINUE_TYPING_TO_SEARCH') || 'Or continue typing to search'}
      </p>
    </div>
  );
}
